import { useState, useEffect, useRef } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export function useRealtimeNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Conectar ao SSE
    const eventSource = new EventSource(`${API}/api/notifications/stream?token=${token}`);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      console.log('🔌 Conectado ao sistema de notificações');
      setIsConnected(true);
    };

    eventSource.onmessage = (event) => {
      try {
        const notification = JSON.parse(event.data);

        // ⭐ CORRIGIDO: o evento de confirmação de conexão não é uma notificação de verdade
        if (notification.type === 'connected') return;

        // Adicionar notificação
        setNotifications(prev => [notification, ...prev].slice(0, 50));

        // Mostrar notificação no navegador
        if (Notification.permission === 'granted') {
          new Notification(notification.title || notification.message?.title || 'Nova notificação', {
            body: notification.message?.message || notification.message || '',
            icon: notification.icon || notification.message?.icon || '/logo.png'
          });
        }

        // Tocar som de notificação (respeitando a preferência do usuário)
        playNotificationSound();
      } catch (err) {
        console.error('Erro ao processar notificação:', err);
      }
    };

    eventSource.onerror = async (err) => {
      console.error('Erro no SSE:', err);
      setIsConnected(false);

      // NOVO: o EventSource nativo tenta reconectar sozinho automaticamente,
      // mesmo com sessao invalida (401) - isso gerava loop infinito de tentativas
      // e poluia o log do backend. Fazemos uma checagem rapida numa rota autenticada
      // normal (que ja passa pelo interceptor "nuclear" do axios) para descobrir
      // se a queda foi por sessao invalida ou so uma instabilidade de rede comum.
      try {
        const currentToken = localStorage.getItem('token');
        if (!currentToken) {
          eventSource.close();
          return;
        }

        const checkRes = await fetch(`${API}/api/profile/me`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });

        if (checkRes.status === 401) {
          // Sessao realmente invalida - fecha de vez e forca logout,
          // igual o interceptor do axios ja faz para chamadas normais
          eventSource.close();
          localStorage.clear();
          sessionStorage.clear();
          window.location.replace('/login');
          return;
        }
      } catch (checkErr) {
        // Falha na checagem (ex: sem internet) - deixa o EventSource
        // tentar reconectar sozinho normalmente, sem forcar logout
      }
    };

    // Solicitar permissão para notificações
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // ⭐ NOVO: som sintetizado por código (sem precisar de arquivo de áudio nenhum),
  // com dois tons rápidos, parecido com um "ding" de notificação comum.
  // Respeita a preferência do usuário (users.notification_sound_enabled).
  const playNotificationSound = () => {
    try {
      // Confere a preferência salva localmente (atualizada no login/perfil)
      const soundEnabled = localStorage.getItem('notification_sound_enabled');
      if (soundEnabled === 'false') return;

      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const ctx = new AudioContext();

      const playTone = (freq, startTime, duration) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.connect(gain);
        gain.connect(ctx.destination);
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(0.25, startTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
        osc.start(startTime);
        osc.stop(startTime + duration);
      };

            // Padrão "ding-dong" de duas notas descendentes, mais parecido com
      // notificação de mensagem (nota mais aguda, depois uma mais grave, rápido)
      const now = ctx.currentTime;
      playTone(1318.51, now, 0.15);         // E6 - nota aguda
      playTone(987.77, now + 0.12, 0.25);   // B5 - nota mais grave, sustenta um pouco mais
    } catch (err) {
      // Silencioso — som é só um extra, nunca deve quebrar a experiência
    }
  };

  const clearNotifications = () => setNotifications([]);

  return {
    notifications,
    isConnected,
    clearNotifications,
    unreadCount: notifications.filter(n => !n.read).length
  };
}
